import i18n from "i18next";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Button } from "@/components/lilia-ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/lilia-ui/dropdown-menu";
import {
  IconCopy,
  IconChevronDown,
  IconCheck,
  IconPalette,
} from "@tabler/icons-react";
import { cn } from "@/lib/cn";
import { usePreviewApi } from "@/hooks/usePreviewApi";
import { latexLanguage } from "@/lib/codemirror/latex-language";
import { rhythmText } from "@/lib/codemirror/latex-rhythm";
import { SourceCodeViewer, type SourceDensity } from "./SourceCodeViewer";
import {
  DEFAULT_SOURCE_DENSITY,
  SOURCE_DENSITY_PRESETS,
  isSourceDensity,
} from "@/lib/codemirror/source-density";
import { cleanImageUrls, stripImages } from "@/lib/latex-image-utils";
import { parseLatexMarkers, lineToBlockId, allMappedLines, type LineRange } from "./block-line-map";
import styles from "./LaTeXPreview.module.scss";
import { LatexBlocksTab } from "./LatexBlocksTab";
import { LatexPreambleTab } from "./LatexPreambleTab";
import { blockSlices, parsePreamble, splitPreamble } from "./latexView";
import { LatexTabBar, useLatexTab } from "./LatexTabBar";
import type { RenderableBlock } from "@/components/preview/BlockRenderer";

/** localStorage key for the source density preset (S/M/L face pairings). */
const SOURCE_DENSITY_KEY = "lilia-source-density";
const DENSITY_OPTIONS = (["compact", "normal", "comfortable"] as const).map((id) => ({
  id,
  label: SOURCE_DENSITY_PRESETS[id].label,
  name: SOURCE_DENSITY_PRESETS[id].name,
  title: SOURCE_DENSITY_PRESETS[id].title,
}));

interface LaTeXPreviewProps {
  documentId: string;
  refreshKey?: number;
  focusedBlockId?: string | null;
  onScrollToBlock?: (blockId: string) => void;
  /** Told when a fetch starts and ends, for a host's own refresh control. */
  onBusyChange?: (busy: boolean) => void;
  /** The document's blocks, for the Blocks tab's rendered cards. */
  blocks?: (RenderableBlock & { id: string })[];
}

export function LaTeXPreview({ documentId, refreshKey = 0, focusedBlockId, onScrollToBlock, onBusyChange, blocks = [] }: LaTeXPreviewProps) {
  const [latexTab, setLatexTab] = useLatexTab();
  // A block's "lines a–b" opens Code with those lines tinted, until another block is selected.
  const [codeRange, setCodeRange] = useState<LineRange | undefined>(undefined);
  useEffect(() => setCodeRange(undefined), [focusedBlockId]);
  const onBusyChangeRef = useRef(onBusyChange);
  onBusyChangeRef.current = onBusyChange;
  const [rawLatex, setRawLatex] = useState<string>("");
  // `isInitialLoad` is true only before the first successful fetch.
  // Subsequent refreshes don't flip back — the current rawLatex stays
  // rendered while the new one arrives, so the pane doesn't blank to
  // "Loading…" on every save. Fixes John's "flicker on refresh"
  // report (2026-05-13 LaTeX sync + save UX doc).
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  // `justRefreshed` flips true for ~1.2s right AFTER each successful
  // post-initial-load refetch. Drives the "Refreshed ✓" toolbar tick
  // so the user gets positive feedback that the realtime sync just
  // worked, without an in-progress indicator stealing attention
  // (user direction 2026-05-13: "more like refresh success").
  const [justRefreshed, setJustRefreshed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | false>(false);
  // S/M/L = per-face pairings (not a uniform scale). Default M. Persisted.
  const [density, setDensity] = useState<SourceDensity>(() => {
    try {
      const v = localStorage.getItem(SOURCE_DENSITY_KEY);
      return isSourceDensity(v) ? v : DEFAULT_SOURCE_DENSITY;
    } catch {
      return DEFAULT_SOURCE_DENSITY;
    }
  });
  const setDensityPersisted = useCallback((d: SourceDensity) => {
    setDensity(d);
    try { localStorage.setItem(SOURCE_DENSITY_KEY, d); } catch { /* ignore */ }
  }, []);
  // Pane + focused-block background combos (light-mode exploration).
  // Auto-refresh the preview when Ask Lilia applies edits to this document.
  // Without this the LaTeX/preview goes stale after an AI edit until the user
  // hits the manual refresh — part of making "refresh editor" an in-place sync.
  const [appliedTick, setAppliedTick] = useState(0);
  useEffect(() => {
    const onApplied = (e: Event) => {
      const evt = e as CustomEvent<{ documentId?: string }>;
      if (!evt.detail?.documentId || evt.detail.documentId === documentId) {
        setAppliedTick((t) => t + 1);
      }
    };
    window.addEventListener("lilia:document-applied", onApplied);
    return () => window.removeEventListener("lilia:document-applied", onApplied);
  }, [documentId]);

  const previewApi = usePreviewApi();

  useEffect(() => {
    let cancelled = false;

    const wasInitial = isInitialLoad;
    async function fetchLatex() {
      setError(null);
      onBusyChangeRef.current?.(true);
      try {
        const response = await previewApi.getLatex(documentId);
        if (!cancelled) {
          setRawLatex(response.content);
          setIsInitialLoad(false);
          // Flash the ✓ tick only on post-initial refetches —
          // the first load already has its own loading state.
          if (!wasInitial) {
            setJustRefreshed(true);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load LaTeX");
        }
      } finally {
        if (!cancelled) onBusyChangeRef.current?.(false);
      }
    }

    fetchLatex();
    return () => {
      cancelled = true;
    };
    // isInitialLoad intentionally excluded — only refreshKey / appliedTick /
    // documentId / previewApi should trigger refetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, previewApi, refreshKey, appliedTick]);

  // Decay the ✓ tick after 1.2s.
  useEffect(() => {
    if (!justRefreshed) return;
    const id = setTimeout(() => setJustRefreshed(false), 1200);
    return () => clearTimeout(id);
  }, [justRefreshed]);

  // Clean image URLs, parse block markers, build both line maps
  const { cleanedLatex, cleanLineMap } = useMemo(() => {
    const { cleaned: urlCleaned } = cleanImageUrls(rawLatex);
    const { cleaned, cleanLineMap } = parseLatexMarkers(urlCleaned);
    return { cleanedLatex: cleaned, cleanLineMap };
  }, [rawLatex]);

  // The source as the reader sees it: no block markers or anchors (block-line-map),
  // and the preamble folded by the viewer, opened and closed from its own bar
  // (the user, 11 Oct). The older summary-line collapse and the block-id switch
  // are gone: one way to fold, and block ids were for debugging.
  const displayLatex = cleanedLatex;
  // Where each block starts, for the view's fixed gaps and for Copy (Olivia, 11 Oct).
  const blockStarts = useMemo(() => [...cleanLineMap.values()].map((r) => r.start), [cleanLineMap]);
  const lineMap = cleanLineMap;

  const clickableLines = useMemo(() => allMappedLines(lineMap), [lineMap]);

  const highlightRange = useMemo<LineRange | undefined>(() => {
    if (codeRange) return codeRange;
    if (!focusedBlockId) return undefined;
    return lineMap.get(focusedBlockId) ?? undefined;
  }, [codeRange, focusedBlockId, lineMap]);

  const titleIds = useMemo(() => new Set(blocks.filter((b) => b.type === "title").map((b) => b.id)), [blocks]);
  const slices = useMemo(() => blockSlices(cleanedLatex, lineMap, titleIds), [cleanedLatex, lineMap, titleIds]);
  const preamble = useMemo(() => parsePreamble(splitPreamble(cleanedLatex).preamble), [cleanedLatex]);
  const openLines = useCallback((r: LineRange) => { setCodeRange(r); setLatexTab("code"); }, [setLatexTab]);
  const editPreamble = useCallback(() => {
    window.dispatchEvent(new CustomEvent("lilia:open-document-settings", { detail: { focus: "preamble" } }));
  }, []);

  const handleLineClick = useCallback((lineNum: number) => {
    if (!onScrollToBlock) return;
    const blockId = lineToBlockId(lineNum, lineMap);
    if (blockId) onScrollToBlock(blockId);
  }, [lineMap, onScrollToBlock]);

  const showCopiedFeedback = useCallback((label: string) => {
    setCopied(label);
    setTimeout(() => setCopied(false), 2000);
  }, []);

  const handleCopy = useCallback(async () => {
    try {
      // What is shown: one blank line between blocks.
      await navigator.clipboard.writeText(rhythmText(cleanedLatex, blockStarts));
      showCopiedFeedback("Copied!");
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  }, [cleanedLatex, blockStarts, showCopiedFeedback]);

  const handleCopyWithUrls = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(rawLatex);
      showCopiedFeedback("Copied with URLs!");
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  }, [rawLatex, showCopiedFeedback]);

  const handleCopyWithoutImages = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(stripImages(rhythmText(cleanedLatex, blockStarts)));
      showCopiedFeedback("Copied without images!");
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  }, [cleanedLatex, blockStarts, showCopiedFeedback]);

  // Only show "Loading…" on the FIRST fetch. On subsequent refreshes
  // (post-save), keep the stale content rendered until the new
  // content lands — no flicker.
  if (isInitialLoad) {
    return (
      <div className={styles.loading}>
        <span>{i18n.t("editor:latexView.loadingLatex")}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <span>{error}</span>
      </div>
    );
  }

  const tabsRow = <LatexTabBar tab={latexTab} onChange={setLatexTab} />;

  if (latexTab !== "code") {
    return (
      <div data-testid="latex-preview" data-latex-tab={latexTab} className={styles.container}>
        {tabsRow}
        {latexTab === "blocks" ? (
          <LatexBlocksTab
            documentId={documentId}
            slices={slices}
            blocks={blocks}
            focusedBlockId={focusedBlockId}
            onScrollToBlock={onScrollToBlock}
            onOpenLines={openLines}
          />
        ) : (
          <LatexPreambleTab summary={preamble} onEditPreamble={editPreamble} />
        )}
      </div>
    );
  }

  return (
    <div data-testid="latex-preview" data-latex-tab="code" className={styles.container}>
      {tabsRow}
      <div className={styles.toolbar}>
        {/* Refresh-success tick — small ✓ + i18n.t("editor:latexView.refreshed") label that
            flashes for 1.2s after each successful post-initial-load
            refetch. Lives BEFORE the controls cluster so the toolbar's
            `justify-content: flex-end` keeps both right-aligned with
            no gap. User direction 2026-05-13: "more like refresh
            success"; "the refreshed pushes to the right" (fixed). */}
        {justRefreshed && (
          <div
            data-testid="latex-refreshed"
            className="inline-flex items-center gap-1 text-[11px] mr-2 lilia-save-flash self-center"
            style={{ color: "var(--lilia-success, #16a34a)" }}
            aria-live="polite"
          >
            <IconCheck size={12} />
            <span>{i18n.t("editor:latexView.refreshed")}</span>
          </div>
        )}
        {/* Type size — polished segmented control (S / M / L face pairings). */}
        <div
          data-testid="source-density-toggle"
          className={styles.sizeSegment}
          role="group"
          aria-label={i18n.t("editor:latexView.sourceTextSize")}
        >
          {DENSITY_OPTIONS.map((opt) => {
            const active = density === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                data-testid={`source-density-${opt.id}`}
                data-active={active}
                onClick={() => setDensityPersisted(opt.id)}
                title={opt.title}
                aria-pressed={active}
                className={cn(styles.sizeBtn, active && styles.sizeBtnActive)}
              >
                <span className={styles.sizeLetter} data-size={opt.label}>
                  {opt.label}
                </span>
              </button>
            );
          })}
        </div>

        <div className="inline-flex items-center gap-1 ml-1">
          <Button
            variant="subtle"
            size="sm"
            onClick={handleCopy}
            title={i18n.t("editor:latexView.copyLatexWithCleanImage")}
          >
            <IconCopy size={16} /> {copied || "Copy"}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="subtle" size="icon-xs" title={i18n.t("editor:latexView.copyOptions")}>
                <IconChevronDown size={14} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="z-[10100]">
              <DropdownMenuItem onClick={handleCopy}>
                Copy (clean paths)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleCopyWithUrls}>
                {i18n.t("editor:latexView.copyWithImageUrls")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleCopyWithoutImages}>
                {i18n.t("editor:latexView.copyWithoutImages")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {/* Refresh-success tick — a small ✓ + i18n.t("editor:latexView.refreshed") label that
            flashes for 1.2s right AFTER each successful refetch.
            Mirrors the TopBar save pip pattern (PR #108). User
            direction 2026-05-13: "more like refresh success". */}
        {justRefreshed && (
          <div
            data-testid="latex-refreshed"
            className="inline-flex items-center gap-1 text-[11px] ml-auto pr-2 lilia-save-flash"
            style={{ color: "var(--lilia-success, #16a34a)" }}
            aria-live="polite"
          >
            <IconCheck size={12} />
            <span>{i18n.t("editor:latexView.refreshed")}</span>
          </div>
        )}
      </div>
      <SourceCodeViewer
        value={displayLatex}
        language={latexLanguage}
        highlightRange={highlightRange}
        clickableLines={clickableLines}
        onLineClick={handleLineClick}
        density={density}
        foldPreamble
        rhythm={{ blockStarts }}
      />
    </div>
  );
}
